import { DiffType, GetCssClassNamesOption } from '@ardoq/data-model';
import { GraphItemId, ItemLabels, RawGraphNode2 } from '../types';
import { formatOtherLabel } from '../labels/otherLabelUtils';
import { GraphItemBase } from './types';
import { GraphInterface } from '../graphInterface/graphInterface';

let unidentifiedInstanceCounter = 0;

export type GraphItemMetadata = {
  isComponent: boolean;
  isReference: boolean;
  isGroup: boolean;
};

type GetComponentGraphNodeMetadataArgs = {
  viewInterface: GraphInterface;
  rawGraphItem: RawGraphNode2;
};
export const getGraphItemMetadata = ({
  viewInterface,
  rawGraphItem,
}: GetComponentGraphNodeMetadataArgs): GraphItemMetadata & {
  isGhost: boolean;
} => {
  const { modelId, isGhost, type } = rawGraphItem;
  if (!modelId) {
    return {
      isComponent: false,
      isReference: false,
      isGroup: Boolean(type),
      isGhost: Boolean(isGhost),
    };
  }
  return {
    isComponent: viewInterface.isComponent(modelId),
    isReference: viewInterface.isReference(modelId),
    isGroup: Boolean(type),
    isGhost: Boolean(isGhost),
  };
};

export abstract class GraphItem implements GraphItemBase {
  readonly modelId: string = '';
  parent?: GraphItem;
  protected hash = '';
  isTransparentized: boolean;
  readonly id: GraphItemId;
  protected opacity = 1;

  private isComponentItem: boolean;
  private isReferenceItem: boolean;
  private isGroupItem: boolean;

  /**
   * Needed for legacy compatibility
   */
  protected label: string = '';

  constructor(
    modelId: string,
    graphItemMetadata: GraphItemMetadata,
    id?: string
  ) {
    this.modelId = modelId;
    this.id = id ?? `n-v2-${unidentifiedInstanceCounter++}`;
    this.isTransparentized = false;
    this.isComponentItem = graphItemMetadata.isComponent;
    this.isReferenceItem = graphItemMetadata.isReference;
    this.isGroupItem = graphItemMetadata.isGroup;
  }

  abstract getVisualDiffType(): DiffType;
  abstract getCSS(_?: GetCssClassNamesOption): string;
  abstract hasURLFields(): boolean;

  abstract setItemLabels(itemLabels: ItemLabels): void;
  abstract getItemLabels(): ItemLabels | undefined;

  /**
   * @deprecated use itemLabels.mainLabel instead
   */
  getLabel() {
    return this.label;
  }
  /**
   * @deprecated use itemLabels.mainLabel instead
   */
  setLabel(label: string) {
    this.label = label;
  }

  /**
   * This is a hack to not have to narrow down the MetamodelViewGraphNode,
   * which is the only class that implements labelWithCount
   */
  getLabelWithCount() {
    return this.getLabel();
  }

  isComponent() {
    return this.isComponentItem;
  }

  isReference() {
    return this.isReferenceItem;
  }

  isGroup() {
    return this.isGroupItem;
  }

  isEqual(otherItem: any) {
    return otherItem instanceof GraphItem && otherItem.hash === this.hash;
  }

  protected getRawHash() {
    const { mainLabel, otherLabels } = this.getItemLabels() ?? {};
    const mainLabelHashSource = mainLabel ?? '';
    const allLabelsHashSource = `${otherLabels?.map(formatOtherLabel).join('|')}${mainLabelHashSource}${this.label}`;

    return `${this.modelId}${this.getParentModelId()}${
      allLabelsHashSource
    }${this.hasURLFields()}${this.getCSS()}`;
  }

  private getParentModelId() {
    return this.parent ? this.parent.modelId : '';
  }

  setOpacity(opacity: number) {
    this.opacity = opacity;
  }

  getOpacity() {
    return this.opacity;
  }
}
